import { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import {
  Modal,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  MenuItem,
  IconButton,
  InputAdornment,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { QrReader } from "react-qr-reader";
import { useSelector } from "react-redux";
import callPiggybackAPI from "../Helpers/piggyback.api.helper";

const useStyles = makeStyles(() => ({
  modalContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "60vh",
    overflowY: "auto",
    width: 400,
    backgroundColor: "#fff",

    boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
    paddingTop: 10,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 10,
    outline: "none",
    borderRadius: 5,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
}));

function TrunInkAddForm({ open, onClose, data, onSubmit, title, schema }) {
  const [formData, setFormData] = useState(data || {});
  const [scanningField, setScanningField] = useState(null);
  const [iconOptions, setIconOptions] = useState([]);
  const selectedSite = useSelector((state) => state.truInk.selectedSite);
  const classes = useStyles();

  useEffect(() => {
    setFormData(data || {});
  }, [data]);

  useEffect(() => {
    const hasIconsField = Object.values(schema || {}).some(
      (f) => f.type === "multiselect-icons"
    );
    if (!hasIconsField || !open || !selectedSite) return;
    callPiggybackAPI(
      "labels",
      { tenantId: selectedSite.tenantId, siteId: selectedSite.id },
      {},
      "GET"
    )
      .then((res) => setIconOptions(Array.isArray(res) ? res : []))
      .catch(() => setIconOptions([]));
  }, [open, schema, selectedSite]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 3,
          width: "95%",
          maxWidth:400,
          maxHeight: "75vh",
          overflowY: "auto",
        }}
      >
        <h3>{title || "Edit Details"}</h3>
        <form onSubmit={handleSubmit} className={classes.formContainer}>
          {Object.keys(schema || {}).map((key) => {
            const fieldSchema = schema[key];
            const value = formData[key] ?? "";

            if (!fieldSchema) return null;

            if (fieldSchema.type === "boolean") {
              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      name={key}
                      checked={!!value}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={fieldSchema.label}
                />
              );
            }

            if (fieldSchema.type === "select") {
              return (
                <TextField
                  key={key}
                  select
                  name={key}
                  label={fieldSchema.label}
                  value={value}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  size="small"
                  variant="outlined"
                  required={fieldSchema.required}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {fieldSchema.options?.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }

            if (fieldSchema.type === "scan") {
              return (
                <Box key={key}>
                  <TextField
                    name={key}
                    label={fieldSchema.label}
                    value={value}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                    margin="normal"
                    required={fieldSchema.required}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setScanningField(key)}
                            title="Scan QR / Barcode"
                          >
                            <QrCodeScannerIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {scanningField === key && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <QrReader
                        constraints={{ facingMode: "environment" }}
                        onResult={(result) => {
                          if (result?.text) {
                            setFormData((prev) => ({ ...prev, [key]: result.text }));
                            setScanningField(null);
                          }
                        }}
                        style={{ width: "100%" }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        sx={{ mt: 1 }}
                        onClick={() => setScanningField(null)}
                      >
                        Cancel Scan
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            }

            if (fieldSchema.type === "multiselect-icons") {
              const selectedNames = Array.isArray(formData[key]) ? formData[key] : [];
              return (
                <FormControl key={key} fullWidth size="small" sx={{ mt: 2 }}>
                  <InputLabel>{fieldSchema.label}</InputLabel>
                  <Select
                    multiple
                    value={selectedNames}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    label={fieldSchema.label}
                  >
                    {iconOptions.map((icon) => (
                      <MenuItem key={icon.id} value={icon.name}>{icon.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            }

            return (
              <TextField
                key={key}
                name={key}
                label={fieldSchema.label}
                value={value}
                onChange={handleChange}
                type={fieldSchema.type}
                variant="outlined"
                size="small"
                fullWidth
                margin="normal"
                required={fieldSchema.required}
              />
            );
          })}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              onClick={onClose}
              variant="contained"
              color="error"
              sx={{
                mr:1,
                textTransform: "none",
                backgroundColor: "rgb(90, 28, 47)",
                "&:hover": {
                  backgroundColor: "rgb(70, 20, 35)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(90, 28, 47, 0.4)",
                  color: "#fff",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: "none",
                background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                color: "#fff",
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                  opacity: 0.95,
                  boxShadow: "none"
                },
                "&.Mui-disabled": {
                  background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                  opacity: 0.5,
                  color: "#fff"
                }
              }}
            >
              Save
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
}

export default TrunInkAddForm;
